/**
 * adapters/http/correlation — identificador de correlação por requisição lógica.
 *
 * O `X-Correlation-Id` é o que permite juntar um erro visto no navegador com a linha
 * correspondente no log do backend. O gateway e os serviços já o consomem; o portal, até
 * LDG-0193, não o emitia: as funções viviam em `adapters/http/interceptors.ts`, um módulo que
 * nenhuma parte de produção injetava. Elas moram aqui agora, e quem as usa é o `ApiClient` —
 * o único caminho HTTP vivo do portal.
 *
 * "Requisição lógica" inclui o reenvio após 401: o id é gerado uma vez por chamada do
 * `ApiClient` e reaproveitado no retry, para que a renovação de token não apareça no log como
 * duas operações sem relação entre si.
 */

/**
 * Gera um identificador de correlação único (UUID v4).
 *
 * `crypto.randomUUID` só existe em contexto seguro — HTTPS ou localhost. Num acesso por HTTP
 * simples (um IP de rede local, por exemplo) ele é `undefined`, e chamá-lo direto lançaria
 * `TypeError` em TODA requisição: o portal inteiro pararia por causa de um header de diagnóstico.
 * O módulo morto que originou este código tinha a mesma chamada nua, e nunca pagou o preço
 * porque nada em produção o executava. O fallback abaixo evita herdar a falha junto com a função.
 */
export function generateCorrelationId(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === "function") return c.randomUUID();

  // Fallback com aleatoriedade criptográfica quando disponível; Math.random como último recurso.
  // Não é preciso qualidade criptográfica aqui — o id serve para juntar log, não para autorizar.
  const bytes = new Uint8Array(16);
  if (typeof c?.getRandomValues === "function") {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  // Marca versão 4 e variante RFC 4122, para que o id seja um UUID v4 legítimo.
  bytes[6] = ((bytes[6] as number) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] as number) & 0x3f) | 0x80;

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Devolve uma cópia dos headers com o `X-Correlation-Id` injetado. Não muta a entrada.
 */
export function applyCorrelationHeader(
  headers: Readonly<Record<string, string>>,
  correlationId: string,
): Record<string, string> {
  return { ...headers, "X-Correlation-Id": correlationId };
}
